import { LeftOutlined } from '@ant-design/icons'
import { Button, Checkbox, Input, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import FoodSafetyCorrectiveActionPanel from 'src/components/food-safety/FoodSafetyCorrectiveActionPanel'
import FoodSafetyDayNav from 'src/components/food-safety/FoodSafetyDayNav'
import {
  FoodSafetyExceptionFollowUp,
  FoodSafetyExceptionStatus,
  NeedsActionBadge,
} from 'src/components/food-safety/FoodSafetyExceptionFollowUp'
import FoodSafetyUnresolvedStrip from 'src/components/food-safety/FoodSafetyUnresolvedStrip'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySubmitErrorBox,
  FoodSafetySubmitErrorLead,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import { ollieFabPageClearanceCss } from 'src/components/ollie/ollieFabPlacement'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_CHECKLIST_STEP_STATUS,
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import useFoodSafetyExceptionFollowUps from 'src/hooks/useFoodSafetyExceptionFollowUps'
import useFoodSafetyLinkedCheck from 'src/hooks/useFoodSafetyLinkedCheck'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import useFoodSafetySubmissionKeys from 'src/hooks/useFoodSafetySubmissionKeys'
import useIsBlocked, { useCanAccess } from 'src/hooks/useIsBlocked'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import useFoodSafetyNewDayReload from 'src/hooks/useFoodSafetyNewDayReload'
import FoodSafetyReportsPaywallModal from 'src/screens/food-safety/FoodSafetyReportsPaywallModal'
import {
  createFoodSafetyLog,
  fetchFoodSafetyDay,
} from 'src/utils/foodSafetyApi'
import { trackFoodSafetyLogSubmitted } from 'src/utils/foodSafetyAnalytics'
import {
  FOOD_SAFETY_NEEDS_ACTION_PARAM,
  countFoodSafetyOpenExceptions,
  formatFoodSafetyNeedsActionBadge,
  focusFoodSafetyCorrectiveField,
  resolveFoodSafetyCorrectiveFocusField,
} from 'src/utils/foodSafetyCorrectiveAction'
import {
  FOOD_SAFETY_SUBMIT_ERROR_LEAD,
  FOOD_SAFETY_SUBMIT_RETRY_LABEL,
  formatFoodSafetySubmitError,
} from 'src/utils/foodSafetyFeedback'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import {
  FOOD_SAFETY_CHECKLIST_ISSUE_MAX_LENGTH,
  FOOD_SAFETY_CHECKLIST_NA_REASON_MAX_LENGTH,
  allChecklistStepsTicked,
  buildChecklistLogBody,
  buildCleaningJobLogBody,
  checklistDraftStepStatus,
  clearChecklistStep,
  countChecklistIssuesWithoutAction,
  emptyChecklistDraft,
  findChecklistStepError,
  findChecklistStepException,
  formatChecklistCompletedMeta,
  formatCleaningDayCardMeta,
  formatChecklistDoneBadge,
  formatChecklistProgressHint,
  formatChecklistSavedMessage,
  formatCleaningDayProgressLabel,
  markChecklistStepIssue,
  markChecklistStepNa,
  normalizeChecklistSteps,
  patchChecklistStep,
  resolveChecklistLoggedStepStatus,
  resolveCleaningEmptyStepsCopy,
  summarizeCleaningDayProgress,
  toggleChecklistStep,
  validateChecklistReadyToSubmit,
} from 'src/utils/foodSafetyCleaning'
import {
  findFramedNextDefinitionId,
  formatFridgeDueBy,
  resolveFoodSafetyLateDueBy,
} from 'src/utils/foodSafetyFridges'
import { isCleaningJob } from 'src/utils/foodSafetySetup'
import {
  buildFoodSafetyBrowseWindow,
  isFoodSafetyViewingToday,
  resolveFoodSafetyScheduleBadge,
  foodSafetyHomeHref,
  formatFoodSafetyOverviewBackLabel,
} from 'src/utils/foodSafetyHome'
import { setupAddButtonLabel } from 'src/utils/foodSafetySetup'
import { resolveFoodSafetyLinkedCheckLine } from 'src/utils/ollieActionDeepLinks'

const SECTION = FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS
const { DONE, NA, ISSUE } = FOOD_SAFETY_CHECKLIST_STEP_STATUS

const LOGGED_STEP_MARK = { [DONE]: '✓', [NA]: '–', [ISSUE]: '⚠' }

const focusTestId = testId => {
  requestAnimationFrame(() => {
    const root = document.querySelector(`[data-testid="${testId}"]`)
    const target = root?.matches?.('input, textarea, button')
      ? root
      : root?.querySelector?.('textarea, input, button')
    target?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    target?.focus?.()
  })
}

const cleaningCardTestId = id => `cleaning-card-${id}`

const FoodSafetyCleaningScreen = () => {
  const blocked = useIsBlocked('food-safety')
  const canManage = useCanAccess('food-safety-manage')
  const history = useHistory()
  const location = useLocation()
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = String(venue?._id || routeVenueId || '')

  const queryDate = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('date') || undefined
  }, [location.search])
  const jumpToNeedsAction = useMemo(
    () =>
      new URLSearchParams(location.search).get(
        FOOD_SAFETY_NEEDS_ACTION_PARAM,
      ) === '1',
    [location.search],
  )

  const [day, setDay] = useState(null)
  const newDayKey = useFoodSafetyNewDayReload(day)
  const [loading, setLoading] = useState(true)
  const [drafts, setDrafts] = useState({}) // definitionId → { [stepKey]: true | false | { status: 'na' | 'issue', … } }
  const [submitErrors, setSubmitErrors] = useState({})
  const [stepErrors, setStepErrors] = useState({}) // definitionId → { stepKey, message }
  const [submittingId, setSubmittingId] = useState(null)
  const [historyPaywallOpen, setHistoryPaywallOpen] = useState(false)
  const [lockAccess, setLockAccess] = useState(null)
  const latestDay = useFoodSafetyLatestRequest()
  const saveLock = useFoodSafetySaveLock()
  const submissionKeys = useFoodSafetySubmissionKeys()

  const loadDay = useCallback(async () => {
    if (!venueId) return
    const isCurrent = latestDay.begin()
    setLoading(true)
    try {
      const data = await fetchFoodSafetyDay(venueId, {
        section: SECTION,
        date: queryDate,
      })
      if (!isCurrent()) return
      setDay(data)
      // Replace drafts for this day — never keep yesterday’s step ticks (29.25)
      setDrafts(() => {
        const next = {}
        for (const item of data.items || []) {
          const id = String(item.definition?._id || '')
          if (!id || item.completed) continue
          next[id] = emptyChecklistDraft(item.definition?.config?.steps)
        }
        return next
      })
      setSubmitErrors({})
    } catch (error) {
      if (!isCurrent()) return
      if (error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
        setLockAccess(error.historyAccess || null)
        setHistoryPaywallOpen(true)
        message.warning(error.message)
        // Stay on this section so the paywall remains visible (29.16).
        const earliest = error.historyAccess?.window?.earliestLocalDate
        if (earliest && earliest !== queryDate) {
          history.replace(
            `/v/${venueId}/food-safety/section/${SECTION}?date=${earliest}`,
          )
        }
        return
      }
      console.error(error)
      message.error(error?.message || 'Could not load cleaning checklists.')
    } finally {
      isCurrent.done()
      if (isCurrent()) setLoading(false)
    }
  }, [venueId, queryDate, history, latestDay])

  useEffect(() => {
    loadDay()
  }, [loadDay, newDayKey])

  // Same day-switch hygiene as fridges (29.25) — don’t carry step ticks across dates
  /** Reload in place; resolves to the new day, or null when a switch won. */
  const refreshDay = useCallback(async () => {
    if (!venueId || !day?.localDate) return null
    const isCurrent = latestDay.beginQuiet()
    if (isCurrent.skipped) return null
    const data = await fetchFoodSafetyDay(venueId, {
      section: SECTION,
      date: day.localDate,
    })
    if (!isCurrent()) return null
    setDay(data)
    return data
  }, [venueId, day?.localDate, latestDay])

  const followUps = useFoodSafetyExceptionFollowUps({
    venueId,
    onSaved: refreshDay,
  })
  const resetFollowUps = followUps.reset

  useEffect(() => {
    setDrafts({})
    setSubmitErrors({})
    setStepErrors({})
    resetFollowUps()
  }, [queryDate, newDayKey, resetFollowUps])

  const browseWindow = useMemo(
    () =>
      buildFoodSafetyBrowseWindow(
        day?.historyAccess || lockAccess,
        day?.localDate,
      ),
    [day?.historyAccess, day?.localDate, lockAccess],
  )

  const goToSectionDate = nextDate => {
    if (!venueId || !nextDate) return
    history.push(
      `/v/${venueId}/food-safety/section/${SECTION}?date=${nextDate}`,
    )
  }

  const openHistoryPaywall = () => setHistoryPaywallOpen(true)

  const updateStepDraft = (definitionId, update) => {
    const clear = prev => {
      if (!prev[definitionId]) return prev
      const next = { ...prev }
      delete next[definitionId]
      return next
    }
    setSubmitErrors(clear)
    setStepErrors(clear)
    setDrafts(prev => ({
      ...prev,
      [definitionId]: update(prev[definitionId] || {}),
    }))
  }

  const setStep = (definitionId, stepKey) =>
    updateStepDraft(definitionId, d => toggleChecklistStep(d, stepKey))

  const markStepNa = (definitionId, stepKey) => {
    updateStepDraft(definitionId, d => markChecklistStepNa(d, stepKey))
    focusTestId(`cleaning-step-na-reason-${definitionId}-${stepKey}`)
  }

  const markStepIssue = (definitionId, stepKey) => {
    updateStepDraft(definitionId, d => markChecklistStepIssue(d, stepKey))
    focusTestId(`cleaning-step-issue-text-${definitionId}-${stepKey}`)
  }

  const clearStep = (definitionId, stepKey) =>
    updateStepDraft(definitionId, d => clearChecklistStep(d, stepKey))

  const patchStep = (definitionId, stepKey, patch) =>
    updateStepDraft(definitionId, d => patchChecklistStep(d, stepKey, patch))

  const submitJob = async item => {
    const definitionId = String(item.definition._id)
    if (!saveLock.tryAcquire(definitionId)) return
    setSubmittingId(definitionId)
    const saveSlot = `${day.localDate}:${definitionId}:job`
    try {
      try {
        await createFoodSafetyLog(
          venueId,
          buildCleaningJobLogBody({
            definitionId,
            localDate: day.localDate,
          }),
          { submissionKey: submissionKeys.keyFor(saveSlot, definitionId) },
        )
      } catch (err) {
        const msg = formatFoodSafetySubmitError(err)
        setSubmitErrors(prev => ({ ...prev, [definitionId]: msg }))
        message.error(msg)
        return
      }
      submissionKeys.clear(saveSlot)
      trackFoodSafetyLogSubmitted({
        venueId,
        section: SECTION,
      })
      message.success(`${item.definition.name || 'Job'} done`)
      setSubmitErrors(prev => {
        if (!prev[definitionId]) return prev
        const next = { ...prev }
        delete next[definitionId]
        return next
      })
      if (!(await refreshAfterFoodSafetySave(refreshDay))) {
        message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
      }
    } finally {
      saveLock.release(definitionId)
      setSubmittingId(null)
    }
  }

  const submitChecklist = async item => {
    const definitionId = String(item.definition._id)
    const steps = normalizeChecklistSteps(item.definition?.config?.steps)
    const draft = drafts[definitionId] || emptyChecklistDraft(steps)
    const stepError = findChecklistStepError(steps, draft)
    const error = validateChecklistReadyToSubmit(steps, draft)
    if (error) {
      if (stepError && allChecklistStepsTicked(steps, draft)) {
        setStepErrors(prev => ({ ...prev, [definitionId]: stepError }))
        if (stepError.field === 'issue') {
          focusTestId(
            `cleaning-step-issue-text-${definitionId}-${stepError.stepKey}`,
          )
        } else {
          focusFoodSafetyCorrectiveField(
            `cleaning-issue-${definitionId}-${stepError.stepKey}`,
            resolveFoodSafetyCorrectiveFocusField(
              draft[stepError.stepKey]?.action,
            ),
          )
        }
        return
      }
      message.warning(error)
      return
    }
    if (!saveLock.tryAcquire(definitionId)) return
    setSubmittingId(definitionId)
    const saveSlot = `${day.localDate}:${definitionId}`
    try {
      try {
        await createFoodSafetyLog(
          venueId,
          buildChecklistLogBody({
            definitionId,
            localDate: day.localDate,
            steps,
            draft,
          }),
          { submissionKey: submissionKeys.keyFor(saveSlot, draft) },
        )
      } catch (err) {
        // 29.37 — keep tick draft; Retry on the card.
        const msg = formatFoodSafetySubmitError(err)
        setSubmitErrors(prev => ({ ...prev, [definitionId]: msg }))
        message.error(msg)
        return
      }
      submissionKeys.clear(saveSlot)
      trackFoodSafetyLogSubmitted({
        venueId,
        section: SECTION,
      })
      const savedMessage = formatChecklistSavedMessage(
        item.definition.name,
        steps,
        draft,
      )
      if (countChecklistIssuesWithoutAction(steps, draft)) {
        message.warning(savedMessage)
      } else {
        message.success(savedMessage)
      }
      setDrafts(prev => {
        const next = { ...prev }
        delete next[definitionId]
        return next
      })
      setSubmitErrors(prev => {
        if (!prev[definitionId]) return prev
        const next = { ...prev }
        delete next[definitionId]
        return next
      })
      if (!(await refreshAfterFoodSafetySave(refreshDay))) {
        message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
      }
    } finally {
      saveLock.release(definitionId)
      setSubmittingId(null)
    }
  }

  const linkedCheck = useFoodSafetyLinkedCheck({
    search: location.search,
    items: day?.items,
    cardTestId: cleaningCardTestId,
    itemsReady: !loading && Boolean(day),
    venueId,
    history,
    onLanded: id => focusTestId(cleaningCardTestId(id)),
  })

  if (blocked) return <PermissionBlockScreen />
  if (!venueId || (loading && !day)) return <Loading />

  const items = day?.items || []
  const framedNextId = findFramedNextDefinitionId(
    items,
    day?.nextOpenDefinitionId,
  )
  const progress = summarizeCleaningDayProgress(items)
  const { openCount, dueTotal } = progress
  const progressLabel = formatCleaningDayProgressLabel(progress)
  const overviewHref = foodSafetyHomeHref(venueId, {
    localDate: day?.localDate,
    todayLocalDate: day?.todayLocalDate,
  })
  const overviewLabel = formatFoodSafetyOverviewBackLabel(
    day?.localDate,
    day?.todayLocalDate,
  )
  const viewingToday = isFoodSafetyViewingToday(
    day?.localDate,
    day?.todayLocalDate,
  )

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={overviewHref} data-testid='fs-section-back'>
          <LeftOutlined /> {overviewLabel}
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>
              {FOOD_SAFETY_SECTION_LABELS[SECTION]}
            </FoodSafetyPageTitle>
            <BodyText>
              {progressLabel ||
                'A job is one Complete. A checklist is tick, then submit. We record who and when.'}
            </BodyText>
          </div>
          {day?.localDate ? (
            <FoodSafetyDayNav
              localDate={day.localDate}
              browseWindow={browseWindow}
              onChangeDate={goToSectionDate}
              onBrowseLocked={openHistoryPaywall}
              disabled={loading}
            />
          ) : null}
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>

      {day?.localDate ? (
        <FoodSafetyUnresolvedStrip
          venueId={venueId}
          section={SECTION}
          viewedLocalDate={day.localDate}
          todayLocalDate={day.todayLocalDate}
          refreshKey={day}
          autoJump={jumpToNeedsAction}
          testIdPrefix='cleaning-unresolved'
        />
      ) : null}

      {items.length === 0 ? (
        <EmptyCard>
          <H3>No cleaning checklists yet</H3>
          <BodyText>
            {canManage
              ? 'Add a checklist in setup so the kitchen can tick it off.'
              : 'Ask a manager to add cleaning checklists in Food Safety setup.'}
          </BodyText>
          {canManage ? (
            <SetupLink
              to={`/v/${venueId}/food-safety/setup?section=cleaning_checklists&add=1`}
            >
              {setupAddButtonLabel(SECTION)}
            </SetupLink>
          ) : null}
        </EmptyCard>
      ) : (
        <CardList>
          {items.map(item => {
            const def = item.definition
            const id = String(def._id)
            const isJob = isCleaningJob(def)
            const steps = isJob
              ? []
              : normalizeChecklistSteps(def.config?.steps)
            const draft = drafts[id] || emptyChecklistDraft(steps)
            const completed = Boolean(item.completed)
            const latestLog = (item.logs || [])[item.logs.length - 1]
            const needsActionBadge = formatFoodSafetyNeedsActionBadge(
              countFoodSafetyOpenExceptions(item.logs),
            )
            const dueLabel = formatFridgeDueBy(def.dueByTime)
            const isNext = !completed && String(framedNextId) === id
            const scheduleBadge = resolveFoodSafetyScheduleBadge(item, {
              isNext,
            })
            const progressHint = formatChecklistProgressHint(steps, draft)
            const linkedLine = resolveFoodSafetyLinkedCheckLine(
              linkedCheck,
              item,
            )
            const ready = allChecklistStepsTicked(steps, draft)
            const emptyStepsCopy = resolveCleaningEmptyStepsCopy({ canManage })

            return (
              <UnitCard
                key={id}
                data-completed={completed ? 'true' : 'false'}
                data-next={isNext ? 'true' : 'false'}
                data-testid={cleaningCardTestId(id)}
                data-ollie-linked={
                  linkedCheck?.definitionId === id ? 'true' : 'false'
                }
              >
                <UnitHeader>
                  <div>
                    <UnitName>{def.name}</UnitName>
                    <MetaLine>
                      {formatCleaningDayCardMeta({
                        isJob,
                        completed,
                        dueLabel,
                        stepCount: steps.length,
                        viewingToday,
                        item,
                      })}
                    </MetaLine>
                  </div>
                  {needsActionBadge ? (
                    <NeedsActionBadge
                      data-testid={`cleaning-needs-action-badge-${id}`}
                    >
                      {needsActionBadge}
                    </NeedsActionBadge>
                  ) : completed ? (
                    <DoneBadge>
                      {formatChecklistDoneBadge(latestLog, {
                        lateDue: resolveFoodSafetyLateDueBy(item),
                      })}
                    </DoneBadge>
                  ) : scheduleBadge?.tone === 'missed' ? (
                    <MissedBadge>{scheduleBadge.label}</MissedBadge>
                  ) : scheduleBadge?.tone === 'quiet' && !linkedLine ? (
                    <DoneBadge>{scheduleBadge.label}</DoneBadge>
                  ) : scheduleBadge?.tone === 'open' ? (
                    <NextBadge>{scheduleBadge.label}</NextBadge>
                  ) : null}
                </UnitHeader>
                {linkedLine ? (
                  <LinkedMissedLine
                    data-testid={`cleaning-linked-missed-${id}`}
                  >
                    {linkedLine}
                  </LinkedMissedLine>
                ) : null}

                {completed ? (
                  <LoggedBlock>
                    <BodyText>
                      Completed — {formatChecklistCompletedMeta(latestLog)}
                    </BodyText>
                    {!isJob &&
                    (latestLog?.payload?.stepCompletions || []).length > 0 ? (
                      <LoggedSteps>
                        {latestLog.payload.stepCompletions.map(step => {
                          const status = resolveChecklistLoggedStepStatus(step)
                          const exception =
                            status === ISSUE
                              ? findChecklistStepException(latestLog, step.key)
                              : null
                          return (
                            <LoggedStep
                              key={step.key}
                              data-status={status || 'open'}
                              data-testid={`cleaning-logged-step-${id}-${step.key}`}
                            >
                              <span>
                                {LOGGED_STEP_MARK[status] || '○'} {step.label}
                                {status === NA
                                  ? ` · N/A${
                                      step.naReason ? ` — ${step.naReason}` : ''
                                    }`
                                  : ''}
                                {!status ? ' · Not done' : ''}
                              </span>
                              {status === ISSUE ? (
                                <LoggedIssue>
                                  Issue: {step.issue || 'not described'}
                                </LoggedIssue>
                              ) : null}
                              {exception ? (
                                <>
                                  <FoodSafetyExceptionStatus
                                    exception={exception}
                                    testId={`cleaning-needs-action-${id}-${step.key}`}
                                  />
                                  <FoodSafetyExceptionFollowUp
                                    exception={exception}
                                    section={SECTION}
                                    viewingToday={viewingToday}
                                    followUps={followUps}
                                    testIdPrefix='cleaning'
                                    rowKey={`${id}-${step.key}`}
                                  />
                                </>
                              ) : null}
                            </LoggedStep>
                          )
                        })}
                      </LoggedSteps>
                    ) : null}
                  </LoggedBlock>
                ) : isJob ? (
                  <>
                    {submitErrors[id] ? (
                      <FoodSafetySubmitErrorBox
                        data-testid={`cleaning-submit-error-${id}`}
                        role='status'
                      >
                        <FoodSafetySubmitErrorLead>
                          {FOOD_SAFETY_SUBMIT_ERROR_LEAD}
                        </FoodSafetySubmitErrorLead>
                        {submitErrors[id]}
                      </FoodSafetySubmitErrorBox>
                    ) : null}
                    <SubmitRow>
                      <Button
                        type='primary'
                        loading={submittingId === id}
                        onClick={() => submitJob(item)}
                        data-testid={`cleaning-complete-${id}`}
                      >
                        {submitErrors[id]
                          ? FOOD_SAFETY_SUBMIT_RETRY_LABEL
                          : 'Complete'}
                      </Button>
                    </SubmitRow>
                  </>
                ) : (
                  <>
                    <StepList>
                      {steps.length === 0 ? (
                        <EmptyStepsBlock
                          data-testid={`cleaning-empty-steps-${id}`}
                        >
                          <BodyText>{emptyStepsCopy.line}</BodyText>
                          {emptyStepsCopy.ctaLabel ? (
                            <SetupLink
                              to={`/v/${venueId}/food-safety/setup?section=${SECTION}`}
                              data-testid={`cleaning-empty-steps-setup-${id}`}
                            >
                              {emptyStepsCopy.ctaLabel}
                            </SetupLink>
                          ) : null}
                        </EmptyStepsBlock>
                      ) : (
                        steps.map(step => {
                          const value = draft[step.key]
                          const status = checklistDraftStepStatus(value)
                          const stepKeyId = `${id}-${step.key}`
                          const stepError =
                            stepErrors[id]?.stepKey === step.key
                              ? stepErrors[id].message
                              : null
                          return (
                            <StepRow
                              key={step.key}
                              data-status={status || 'open'}
                            >
                              <StepMain>
                                <Checkbox
                                  checked={status === DONE}
                                  onChange={() => setStep(id, step.key)}
                                  data-testid={`cleaning-step-${stepKeyId}`}
                                >
                                  {step.label}
                                </Checkbox>
                                {status === NA ? (
                                  <StepTag>N/A</StepTag>
                                ) : status === ISSUE ? (
                                  <StepTag data-tone='issue'>Issue</StepTag>
                                ) : null}
                                <StepOptions>
                                  {status === NA || status === ISSUE ? (
                                    <StepOption
                                      type='button'
                                      onClick={() => clearStep(id, step.key)}
                                      data-testid={`cleaning-step-clear-${stepKeyId}`}
                                    >
                                      Undo
                                    </StepOption>
                                  ) : (
                                    <>
                                      <StepOption
                                        type='button'
                                        onClick={() => markStepNa(id, step.key)}
                                        aria-label={`${step.label}: not applicable`}
                                        data-testid={`cleaning-step-na-${stepKeyId}`}
                                      >
                                        N/A
                                      </StepOption>
                                      <StepOption
                                        type='button'
                                        onClick={() =>
                                          markStepIssue(id, step.key)
                                        }
                                        aria-label={`${step.label}: report an issue`}
                                        data-testid={`cleaning-step-issue-${stepKeyId}`}
                                      >
                                        Issue
                                      </StepOption>
                                    </>
                                  )}
                                </StepOptions>
                              </StepMain>
                              {status === NA ? (
                                <Input
                                  size='small'
                                  value={value.naReason || ''}
                                  maxLength={
                                    FOOD_SAFETY_CHECKLIST_NA_REASON_MAX_LENGTH
                                  }
                                  onChange={e =>
                                    patchStep(id, step.key, {
                                      naReason: e.target.value,
                                    })
                                  }
                                  placeholder='Why not today? (optional)'
                                  aria-label={`${step.label}: N/A reason (optional)`}
                                  data-testid={`cleaning-step-na-reason-${stepKeyId}`}
                                />
                              ) : null}
                              {status === ISSUE ? (
                                <FoodSafetyCorrectiveActionPanel
                                  section={SECTION}
                                  headline={`⚠ Issue — ${step.label}`}
                                  intro={
                                    <IssueField>
                                      <IssueLabel
                                        htmlFor={`cleaning-step-issue-text-${stepKeyId}`}
                                      >
                                        What happened?
                                      </IssueLabel>
                                      <Input.TextArea
                                        id={`cleaning-step-issue-text-${stepKeyId}`}
                                        rows={2}
                                        value={value.issue || ''}
                                        maxLength={
                                          FOOD_SAFETY_CHECKLIST_ISSUE_MAX_LENGTH
                                        }
                                        onChange={e =>
                                          patchStep(id, step.key, {
                                            issue: e.target.value,
                                          })
                                        }
                                        placeholder='e.g. Extractor filter still greasy'
                                        data-testid={`cleaning-step-issue-text-${stepKeyId}`}
                                      />
                                    </IssueField>
                                  }
                                  draft={value.action}
                                  onChange={patch =>
                                    patchStep(id, step.key, { action: patch })
                                  }
                                  saveLaterHint={false}
                                  error={stepError}
                                  testIdPrefix={`cleaning-issue-${stepKeyId}`}
                                />
                              ) : null}
                            </StepRow>
                          )
                        })
                      )}
                    </StepList>
                    {submitErrors[id] ? (
                      <FoodSafetySubmitErrorBox
                        data-testid={`cleaning-submit-error-${id}`}
                        role='status'
                      >
                        <FoodSafetySubmitErrorLead>
                          {FOOD_SAFETY_SUBMIT_ERROR_LEAD}
                        </FoodSafetySubmitErrorLead>
                        {submitErrors[id]}
                      </FoodSafetySubmitErrorBox>
                    ) : null}
                    {steps.length > 0 ? (
                      <SubmitRow>
                        <ProgressHint>{progressHint}</ProgressHint>
                        <Button
                          type='primary'
                          disabled={!ready}
                          loading={submittingId === id}
                          onClick={() => submitChecklist(item)}
                          data-testid={`cleaning-submit-${id}`}
                        >
                          {submitErrors[id]
                            ? FOOD_SAFETY_SUBMIT_RETRY_LABEL
                            : 'Submit'}
                        </Button>
                      </SubmitRow>
                    ) : null}
                  </>
                )}
              </UnitCard>
            )
          })}
        </CardList>
      )}

      {dueTotal > 0 && openCount === 0 ? (
        <AllDone>All cleaning checks done for this day.</AllDone>
      ) : null}

      <FoodSafetyReportsPaywallModal
        visible={historyPaywallOpen}
        reason={FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER}
        olderRecordCount={(day?.historyAccess || lockAccess)?.olderRecordCount}
        upgradeHint={(day?.historyAccess || lockAccess)?.upgradeHint}
        onClose={() => setHistoryPaywallOpen(false)}
      />
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  ${ollieFabPageClearanceCss}

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
    ${ollieFabPageClearanceCss}
  `};
`

const TopBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const EmptyCard = styled(FoodSafetySurfaceCard)`
  border-style: dashed;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const SetupLink = styled(Link)`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  align-self: flex-start;
`

const EmptyStepsBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const UnitCard = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};

  &[data-ollie-linked='true'] {
    outline: 2px solid ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    outline-offset: 2px;
  }
`

const UnitHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-start;
`

const UnitName = styled(H3)`
  margin: 0;
  font-size: 1.05rem;
`

const MetaLine = styled.div`
  margin-top: 0.15rem;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const NextBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
`

const DoneBadge = styled(NextBadge)`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const MissedBadge = styled(NextBadge)`
  color: #c45c26;
`

const LinkedMissedLine = styled.p`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
`

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StepRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .ant-checkbox-wrapper {
    font-size: 0.95rem;
  }
`

const StepMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const StepTag = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  background: #f2f2f2;
  border-radius: 999px;
  padding: 0.05rem 0.5rem;

  &[data-tone='issue'] {
    color: #8a4b00;
    background: #fff4e0;
  }
`

/** Secondary to the tap-to-Done checkbox — small, quiet text buttons. */
const StepOptions = styled.span`
  margin-left: auto;
  display: inline-flex;
  gap: 0.75rem;
`

const StepOption = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const IssueField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const IssueLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
`

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

const ProgressHint = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const LoggedBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const LoggedSteps = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
  font-size: 0.9rem;
`

const LoggedStep = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  & + & {
    margin-top: 0.25rem;
  }
`

const LoggedIssue = styled.span`
  padding-left: 1.1rem;
  color: #8a4b00;
`

const AllDone = styled.p`
  margin-top: ${({ theme }) => theme.spacing.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
`

export default FoodSafetyCleaningScreen
